import { useCallback, useEffect, useState } from "react";
import { FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { DocumentoList } from "./DocumentoList";
import { UploadDocumentDialog } from "./UploadDocumentDialog";
import { DEFAULT_DOCUMENTOS_CONFIG, subcarpetaLabel } from "@/lib/documentos";
import { cn } from "@/lib/utils";
import { documentosService } from "@/services/documentos.service";
import type { Documento, DocumentosConfig } from "@/types";

interface LicitacionExpedienteProps {
  licitacionId: string;
  codigo?: string;
}

/** Expediente documental de una licitación, embebido en su ficha de detalle. */
export function LicitacionExpediente({ licitacionId, codigo }: LicitacionExpedienteProps) {
  const [config, setConfig] = useState<DocumentosConfig>(DEFAULT_DOCUMENTOS_CONFIG);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [subcarpeta, setSubcarpeta] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, docs] = await Promise.all([
        documentosService.config(),
        documentosService.list({ ambito: "licitacion", licitacionId }),
      ]);
      setConfig(cfg);
      setDocumentos(docs);
    } catch {
      toast.error("No se pudo cargar el expediente documental");
    } finally {
      setLoading(false);
    }
  }, [licitacionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibles = subcarpeta ? documentos.filter((d) => d.subcarpeta === subcarpeta) : documentos;

  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FolderOpen className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">
            Expediente documental{codigo ? ` · ${codigo}` : ""}
          </h3>
          <span className="text-xs text-muted-foreground">{documentos.length} archivos</span>
        </div>
        <UploadDocumentDialog
          config={config}
          ambito="licitacion"
          licitacionId={licitacionId}
          defaultSubcarpeta={subcarpeta ?? undefined}
          onUploaded={() => void load()}
          triggerLabel="Adjuntar"
        />
      </header>

      <div className="flex flex-wrap gap-1.5">
        <FilterChip active={subcarpeta === null} onClick={() => setSubcarpeta(null)}>
          Todos ({documentos.length})
        </FilterChip>
        {(config.subcarpetas.licitacion ?? []).map((sub) => (
          <FilterChip key={sub} active={subcarpeta === sub} onClick={() => setSubcarpeta(sub)}>
            {subcarpetaLabel(config, sub)} ({documentos.filter((d) => d.subcarpeta === sub).length})
          </FilterChip>
        ))}
      </div>

      <DocumentoList
        documentos={visibles}
        loading={loading}
        onChanged={() => void load()}
        emptyMessage="Aún no hay documentos en esta carpeta de la licitación."
      />
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs transition",
        active
          ? "border-primary bg-primary/10 text-primary font-medium"
          : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
